/**
 * Tally — aggregate the bulletin and combine trustee partial decryptions.
 * See ADR 0008 § 6 + § 7 and Belenios v2 specification § 6.
 *
 * Two pure operations exposed here:
 *   - aggregateBallots: from a bulletin (list of EncryptedBallot +
 *     receivedAt), apply the Estonian-style supersession rule (last
 *     cast per credential wins), verify each surviving ballot, and
 *     return the homomorphic per-option aggregate ciphertext.
 *   - combineTally: from the aggregate, the qualified subset of
 *     PartialDecryption envelopes, and the DKG transcript, apply
 *     Lagrange-in-exponent and recover each option's vote count via a
 *     precomputed `count*G → count` lookup table.
 *
 * No clock, no randomness, no state.
 */
import { ristretto255 } from "@noble/curves/ed25519";

import type {
  DkgTranscript,
  ElGamalCiphertext,
  EncryptedBallot,
  PartialDecryption,
  RistrettoPoint,
} from "../types";
import { GROUP_ORDER, bytesToHex, encodePoint } from "../internal/encoding";
import { verifyBallot } from "../voter/index";
import { verifyPartialDecryption } from "../trustee/index";

const G = ristretto255.Point.BASE;
const ZERO = ristretto255.Point.ZERO;

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

export interface BulletinEntryInput {
  readonly encrypted: EncryptedBallot;
  /** ISO-8601 timestamp; sorts ascending per credentialPublicKey for revote. */
  readonly receivedAt: string;
}

export interface AggregateBallotsInput {
  readonly ballotId: string;
  readonly bulletin: ReadonlyArray<BulletinEntryInput>;
  readonly ballotPublicKey: RistrettoPoint;
  readonly optionsCount: number;
  /** Number of seats the ballot fills. Each surviving ballot's
   * sum-to-k proof is verified against this value. */
  readonly seats: number;
  /**
   * Optional eligibility predicate. Returns `true` iff `pk` is in the
   * published eligibility list for this ballot. If omitted, eligibility
   * is NOT enforced (acceptable for in-process smoke; the production
   * cast endpoint enforces eligibility before insert anyway).
   */
  readonly isEligible?: (pk: RistrettoPoint) => boolean;
}

export interface AggregateBallotsResult {
  readonly aggregate: ReadonlyArray<ElGamalCiphertext>;
  readonly survivingCount: number;
  readonly supersededCount: number;
  readonly rejectedCount: number;
  readonly errors: ReadonlyArray<string>;
}

export function aggregateBallots(
  input: AggregateBallotsInput,
): AggregateBallotsResult {
  const errors: string[] = [];

  // 1. Filter by ballot identity + verify cryptographically + check eligibility.
  const validEntries: BulletinEntryInput[] = [];
  let rejectedCount = 0;
  for (const e of input.bulletin) {
    if (e.encrypted.ballotId !== input.ballotId) {
      errors.push(
        `entry for ballot ${e.encrypted.ballotId} in bulletin for ${input.ballotId}`,
      );
      rejectedCount++;
      continue;
    }
    if (e.encrypted.ciphertexts.length !== input.optionsCount) {
      errors.push(
        `entry has ${e.encrypted.ciphertexts.length} ciphertexts, expected ${input.optionsCount}`,
      );
      rejectedCount++;
      continue;
    }
    if (input.isEligible && !input.isEligible(e.encrypted.credentialPublicKey)) {
      errors.push("entry credentialPublicKey not in eligibility list");
      rejectedCount++;
      continue;
    }
    const verify = verifyBallot({
      ballot: e.encrypted,
      ballotPublicKey: input.ballotPublicKey,
      seats: input.seats,
    });
    if (!verify.valid) {
      errors.push(
        `entry verifyBallot failed: ${verify.errors.join("; ")}`,
      );
      rejectedCount++;
      continue;
    }
    validEntries.push(e);
  }

  // 2. Estonian-style supersession: last cast per credential wins.
  //    Group by credentialPublicKey hex; keep the entry with the
  //    latest receivedAt. Ties on receivedAt resolve to the later
  //    appearance in `validEntries` (already deterministic if the
  //    bulletin is itself ordered).
  const survivors = new Map<string, BulletinEntryInput>();
  for (const e of validEntries) {
    const key = bytesToHex(encodePoint(e.encrypted.credentialPublicKey));
    const prev = survivors.get(key);
    if (!prev || e.receivedAt >= prev.receivedAt) {
      survivors.set(key, e);
    }
  }
  const supersededCount = validEntries.length - survivors.size;

  // 3. Sum component-wise per option.
  const aggregate: ElGamalCiphertext[] = [];
  for (let opt = 0; opt < input.optionsCount; opt++) {
    let c1: RistrettoPoint = ZERO;
    let c2: RistrettoPoint = ZERO;
    for (const e of survivors.values()) {
      const ct = e.encrypted.ciphertexts[opt]!;
      c1 = c1.add(ct.c1);
      c2 = c2.add(ct.c2);
    }
    aggregate.push({ c1, c2 });
  }

  return {
    aggregate,
    survivingCount: survivors.size,
    supersededCount,
    rejectedCount,
    errors,
  };
}

// ---------------------------------------------------------------------------
// Combine
// ---------------------------------------------------------------------------

export interface CombineTallyInput {
  readonly ballotId: string;
  readonly transcript: DkgTranscript;
  readonly aggregate: ReadonlyArray<ElGamalCiphertext>;
  /**
   * Trustee partial decryptions. At least `transcript.threshold` of
   * them are required to combine. If more are given, the first
   * `threshold` (sorted by trustee index) are used.
   */
  readonly partials: ReadonlyArray<PartialDecryption>;
  /**
   * Maximum count expected for any option. Used to size the
   * `count*G → count` lookup table. For pilot consultativ cap at the
   * eligibility list size; for full OAR cap at ~10000.
   */
  readonly maxCount: number;
}

export interface CombineTallyResult {
  readonly counts: ReadonlyArray<number>;
  readonly trusteeSubset: ReadonlyArray<string>;
  readonly errors: ReadonlyArray<string>;
}

export function combineTally(input: CombineTallyInput): CombineTallyResult {
  const errors: string[] = [];
  const t = input.transcript.threshold;

  // Verify and align partials with the transcript trustees.
  const trusteeById = new Map<string, (typeof input.transcript.trustees)[number]>();
  for (const tr of input.transcript.trustees) trusteeById.set(tr.id, tr);

  const verified: Array<{
    trusteeId: string;
    index: number;
    perOption: PartialDecryption["perOption"];
  }> = [];
  for (const p of input.partials) {
    const tr = trusteeById.get(p.trusteeId);
    if (!tr) {
      errors.push(`partial from unknown trustee ${p.trusteeId}`);
      continue;
    }
    const v = verifyPartialDecryption({
      ballotId: input.ballotId,
      partial: p,
      aggregate: input.aggregate,
      verificationKey: tr.verificationKey,
    });
    if (!v.valid) {
      errors.push(`partial from ${p.trusteeId} failed: ${v.errors.join("; ")}`);
      continue;
    }
    verified.push({ trusteeId: p.trusteeId, index: tr.index, perOption: p.perOption });
  }

  if (verified.length < t) {
    errors.push(
      `only ${verified.length} valid partials, need at least threshold ${t}`,
    );
    return { counts: [], trusteeSubset: [], errors };
  }

  // Pick the first `threshold` partials by trustee index for determinism.
  verified.sort((a, b) => a.index - b.index);
  const subset = verified.slice(0, t);
  const indices = subset.map((s) => s.index);

  // Lagrange basis at x = 0 over the subset's indices.
  const lagrange = indices.map((idx) =>
    lagrangeAtZero(idx, indices),
  );

  // Decrypt each option:
  //   M_j  = sum_t L_t * decryptionShare_{t,j}
  //   m_j  = C2_j - M_j   (a multiple of G; recover the scalar via lookup)
  const table = buildCountTable(input.maxCount);
  const counts: number[] = [];
  for (let opt = 0; opt < input.aggregate.length; opt++) {
    let M: RistrettoPoint = ZERO;
    for (let i = 0; i < subset.length; i++) {
      M = M.add(subset[i]!.perOption[opt]!.decryptionShare.multiplyUnsafe(lagrange[i]!));
    }
    const plaintext = input.aggregate[opt]!.c2.subtract(M);
    const k = lookupCount(plaintext, table);
    if (k === undefined) {
      errors.push(
        `option ${opt} plaintext not in [0, ${input.maxCount}] — bulletin tampered or maxCount too low`,
      );
      counts.push(-1);
    } else {
      counts.push(k);
    }
  }

  return {
    counts,
    trusteeSubset: subset.map((s) => s.trusteeId),
    errors,
  };
}

/**
 * Lagrange basis polynomial `L_t(0)` over the subset of indices,
 * evaluated at x = 0. Used for "secret in the exponent" reconstruction.
 *
 *   L_t(0) = prod_{k in S, k != t} (0 - k) / (t - k)  mod q
 */
export function lagrangeAtZero(
  target: number,
  indices: ReadonlyArray<number>,
): bigint {
  let num = 1n;
  let den = 1n;
  for (const k of indices) {
    if (k === target) continue;
    num = (num * BigInt(-k)) % GROUP_ORDER;
    den = (den * BigInt(target - k)) % GROUP_ORDER;
  }
  return mulMod(normalize(num), invertModPrime(normalize(den), GROUP_ORDER));
}

function normalize(s: bigint): bigint {
  return ((s % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER;
}

function mulMod(a: bigint, b: bigint): bigint {
  return (a * b) % GROUP_ORDER;
}

function invertModPrime(a: bigint, p: bigint): bigint {
  // Fermat: a^(p-2) mod p. p is the Ristretto group order (prime).
  let exp = p - 2n;
  let base = ((a % p) + p) % p;
  let acc = 1n;
  while (exp > 0n) {
    if (exp & 1n) acc = (acc * base) % p;
    base = (base * base) % p;
    exp >>= 1n;
  }
  return acc;
}

// ---------------------------------------------------------------------------
// Count table — `count*G → count` lookup (precomputed)
// ---------------------------------------------------------------------------

/**
 * Build a map from canonical-encoded `k*G` (32 bytes hex) to `k` for
 * `k = 0 .. maxCount`. Used by `combineTally` to recover per-option
 * vote counts from the decrypted plaintext point. `O(maxCount)` work
 * up-front; `O(1)` lookup per option afterwards.
 */
export function buildCountTable(maxCount: number): Map<string, number> {
  if (!Number.isInteger(maxCount) || maxCount < 0) {
    throw new Error("maxCount must be a non-negative integer");
  }
  const table = new Map<string, number>();
  let acc: RistrettoPoint = ZERO;
  table.set(bytesToHex(encodePoint(acc)), 0);
  for (let k = 1; k <= maxCount; k++) {
    acc = acc.add(G);
    table.set(bytesToHex(encodePoint(acc)), k);
  }
  return table;
}

export function lookupCount(
  point: RistrettoPoint,
  table: ReadonlyMap<string, number>,
): number | undefined {
  return table.get(bytesToHex(encodePoint(point)));
}
